// FormRowActions — the templates table's Actions cell: a kebab Dropdown of the
// per-form actions. Edit (opens the form in the builder, the same path a row
// click takes), Bookmark / Remove bookmark (PATCH /forms/bookmark), Move to
// folder (PATCH /forms/update-forms-folder, via the page's picker) and the
// lifecycle verbs — Suspend/Activate, Archive, Restore (PATCH
// /forms/bulk-action) — are live, as are Duplicate (POST /forms/duplicate/:id)
// and Delete (POST /forms/bulk-delete, which moves the form to
// Trash). Every item except Bookmark is an OPTIONAL
// callback: the page passes `undefined` when the verb is missing — or doesn't
// apply on the active tab — and the item (with any orphaned divider) simply
// doesn't render; absence means hidden. Bookmark is a per-user favourite and
// always present, so the kebab never empties. Owns its own open state (one
// row's menu at a time); the flows live in FormTemplatesPage via the callbacks.

import { useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface FormRowActionsProps {
  /** Edit — opens this form in the builder. Absent = hidden. */
  onEdit?: () => void;
  /** Bookmark, or un-bookmark when `bookmarked` — PATCH /forms/bookmark either
   *  way; the page picks the body's `bookmark` value. Always present. */
  onBookmark: () => void;
  /** This row is known to be bookmarked (the Bookmarks tab, where membership in
   *  the filtered list IS the state — the rows carry no bookmark flag of their
   *  own). Swaps the menu item's label + glyph, nothing else. */
  bookmarked?: boolean;
  /** Move to folder — opens the page's picker for this one form. Absent = hidden. */
  onMoveFolder?: () => void;
  /** Duplicate — POST /forms/duplicate/:id. Absent = hidden (the Archived tab). */
  onDuplicate?: () => void;
  /** Suspend, or activate when `suspended` — PATCH /forms/bulk-action either
   *  way; the page picks the verb. Absent = hidden (the Archived tab). */
  onSuspendActive?: () => void;
  /** This row is suspended — swaps the menu item's label + glyph, nothing else
   *  (the `bookmarked` pattern: the page resolves the state, the cell stays
   *  dumb). */
  suspended?: boolean;
  /** Archive — PATCH /forms/bulk-action. Absent = hidden (the Archived tab). */
  onArchive?: () => void;
  /** Restore — PATCH /forms/bulk-action. Archived tab only; absent = hidden. */
  onRestore?: () => void;
  /** Delete — POST /forms/bulk-delete via the page's confirm dialog; moves the
   *  form to Trash. Archived tab only; absent = hidden. */
  onDelete?: () => void;
}

const FormRowActions = ({
  onEdit,
  onBookmark,
  bookmarked = false,
  onMoveFolder,
  onDuplicate,
  onSuspendActive,
  suspended = false,
  onArchive,
  onRestore,
  onDelete,
}: FormRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // The table row navigates to the builder on click — keep the menu's clicks
  // from bubbling into it.
  const stop = (e: MouseEvent) => e.stopPropagation();

  const item = (
    label: string,
    icon: string,
    onClick: () => void,
    opts: { danger?: boolean } = {},
  ): ReactNode => (
    <button
      type='button'
      className={`app-dropdown__item${opts.danger ? ' app-dropdown__item--danger' : ''}`}
      role='menuitem'
      onClick={() => {
        setOpen(false);
        onClick();
      }}
    >
      <Icon name={icon} size={15} />
      <span className='app-dropdown__item-label'>{label}</span>
    </button>
  );

  return (
    <span className='forms-table__actions' onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        width={220}
        portal
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('forms_col_actions')}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          />
        }
      >
        {onEdit && item(t('forms_action_edit'), 'pencil', onEdit)}
        {onDuplicate && item(t('forms_action_duplicate'), 'copy', onDuplicate)}

        {(onEdit || onDuplicate) && <div className='app-dropdown__divider' role='separator' />}

        {onMoveFolder && item(t('forms_action_move_folder'), 'folder-input', onMoveFolder)}
        {bookmarked
          ? item(t('forms_action_unbookmark'), 'bookmark-x', onBookmark)
          : item(t('forms_action_bookmark'), 'bookmark', onBookmark)}
        {onSuspendActive &&
          (suspended
            ? item(t('forms_action_activate'), 'circle-play', onSuspendActive)
            : item(t('forms_action_suspend'), 'circle-pause', onSuspendActive))}

        {(onArchive || onRestore || onDelete) && (
          <div className='app-dropdown__divider' role='separator' />
        )}

        {onArchive && item(t('forms_action_archive'), 'archive', onArchive)}
        {onRestore && item(t('forms_action_restore'), 'archive-restore', onRestore)}
        {onDelete && item(t('forms_action_delete'), 'trash-2', onDelete, { danger: true })}
      </Dropdown>
    </span>
  );
};

export default FormRowActions;
